import type { PostHog } from "posthog-js";
import { readAnalyticsConfig, type AnalyticsConfig } from "./config";
import { ANALYTICS_PRIVATE_ATTRIBUTE } from "./AnalyticsPrivate";
import { readCampaignProperties } from "./campaign";
import { sanitizePathname, stripUrlPropertiesInPlace } from "./url";

const config: AnalyticsConfig = readAnalyticsConfig();

type ClientState = {
  posthog: PostHog | null;
  initPromise: Promise<void> | null;
  optedOut: boolean;
  excluded: boolean;
  accountRole: string | null;
  sessionReplayEligibleThisSession: boolean | null;
};

const state: ClientState = {
  posthog: null,
  initPromise: null,
  optedOut: false,
  excluded: false,
  accountRole: null,
  sessionReplayEligibleThisSession: null,
};

function devWarn(context: string, error: unknown) {
  if (import.meta.env.DEV) {
    console.warn(`[analytics] ${context}`, error);
  }
}

function buildSessionRecordingConfig() {
  return {
    maskAllInputs: true,
    maskInputOptions: { password: true },
    maskTextClass: "ph-mask",
    blockClass: "ph-no-capture",
    blockSelector: `[${ANALYTICS_PRIVATE_ATTRIBUTE}]`,
    recordCrossOriginIframes: false,
  };
}

/**
 * Loads and configures posthog-js. Never called before consent — the only
 * caller is analytics/index.ts's initializeAnalytics(), itself only invoked
 * once optional-storage consent is accepted.
 */
export function ensureInitialized(): Promise<void> {
  if (!config.enabled || !config.apiKey || !config.apiHost) {
    return Promise.resolve();
  }
  if (state.initPromise) return state.initPromise;

  state.initPromise = import("posthog-js")
    .then(({ default: posthog }) => {
      state.optedOut = true;
      posthog.init(config.apiKey!, {
        api_host: config.apiHost!,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: true,
        // Independent of `autocapture: false` — that only governs click/pageview
        // autocapture. Exception capture has its own opt-in and stack traces
        // carry no user content, so it's safe under the privacy rules in §9.
        capture_exceptions: true,
        disable_session_recording: true,
        person_profiles: "identified_only",
        session_recording: buildSessionRecordingConfig(),
        save_campaign_params: false,
        before_send: (captureResult) => {
          if (!captureResult) return captureResult;
          captureResult.properties = stripUrlPropertiesInPlace({
            ...captureResult.properties,
          });
          return captureResult;
        },
        loaded: () => {
          state.posthog = posthog;
        },
      });
      registerCampaignProperties(posthog);
    })
    .catch((error) => {
      devWarn("failed to initialize PostHog", error);
      state.initPromise = null;
    });

  return state.initPromise;
}

function registerCampaignProperties(posthog: PostHog) {
  const campaignProperties = readCampaignProperties();
  if (Object.keys(campaignProperties).length === 0) return;
  try {
    posthog.register_once(campaignProperties);
  } catch (error) {
    devWarn("register campaign properties failed", error);
  }
}

export function isReady(): boolean {
  return Boolean(state.posthog) && !state.optedOut && !state.excluded;
}

export function optIn() {
  try {
    if (state.excluded) return;
    state.posthog?.opt_in_capturing();
    state.optedOut = false;
  } catch (error) {
    devWarn("opt_in_capturing failed", error);
  }
}

export function optOutAndStopRecording() {
  state.optedOut = true;
  state.excluded = false;
  state.sessionReplayEligibleThisSession = null;
  try {
    state.posthog?.stopSessionRecording();
  } catch (error) {
    devWarn("stopSessionRecording failed", error);
  }
  try {
    state.posthog?.opt_out_capturing();
  } catch (error) {
    devWarn("opt_out_capturing failed", error);
  }
}

export function resetIdentity() {
  const shouldReenableAfterAdmin = state.excluded;
  let reenabledAfterAdmin = false;
  try {
    state.posthog?.stopSessionRecording();
  } catch (error) {
    devWarn("stopSessionRecording failed", error);
  }
  try {
    state.posthog?.reset(true);
  } catch (error) {
    devWarn("reset failed", error);
  }
  if (shouldReenableAfterAdmin) {
    try {
      state.posthog?.opt_in_capturing();
      reenabledAfterAdmin = true;
    } catch (error) {
      devWarn("opt_in_capturing failed", error);
    }
  }
  state.accountRole = null;
  state.sessionReplayEligibleThisSession = null;
  state.excluded = false;
  if (shouldReenableAfterAdmin) state.optedOut = !reenabledAfterAdmin;
}

export function excludeCurrentIdentity() {
  state.excluded = true;
  state.optedOut = true;
  state.sessionReplayEligibleThisSession = null;
  try {
    state.posthog?.stopSessionRecording();
  } catch (error) {
    devWarn("stopSessionRecording failed", error);
  }
  try {
    state.posthog?.opt_out_capturing();
  } catch (error) {
    devWarn("exclude current identity failed", error);
  }
}

export function capture(
  event: string,
  properties: Record<string, string | number | boolean>
) {
  if (!isReady()) return;
  try {
    state.posthog?.capture(event, {
      ...properties,
      ...readCampaignProperties(),
      platform: "web",
      surface: "app",
    });
  } catch (error) {
    devWarn(`capture(${event}) failed`, error);
  }
}

export function capturePageview(pathname: string) {
  if (!isReady()) return;
  try {
    state.posthog?.capture("$pageview", {
      $current_url: sanitizePathname(pathname),
      platform: "web",
      surface: "app",
      ...readCampaignProperties(),
    });
  } catch (error) {
    devWarn("capturePageview failed", error);
  }
}

export function identify(
  distinctId: string,
  properties: Record<string, string | number | boolean>
) {
  if (!state.posthog || (state.optedOut && !state.excluded)) return;
  try {
    state.posthog?.identify(distinctId, properties);
  } catch (error) {
    devWarn("identify failed", error);
  }
  state.accountRole =
    typeof properties.account_role === "string"
      ? properties.account_role
      : null;
  if (state.accountRole === "ADMIN") {
    excludeCurrentIdentity();
    return;
  }
  applySessionReplayEligibility();
}

/**
 * Session replay is only ever (re-)evaluated from identify(), never from
 * ensureInitialized()'s `loaded` callback. This means recording never starts
 * for an anonymous/pre-login session — the account role is unknown until
 * identify() runs — which is the deliberate, stricter trade-off: it costs
 * replay coverage of pre-login pages, but guarantees an ADMIN account is
 * never recorded even for an instant.
 */
function applySessionReplayEligibility() {
  if (!isReady()) return;
  const { enabled, sampleRate } = config.sessionReplay;
  if (!enabled) {
    try {
      state.posthog?.stopSessionRecording();
    } catch (error) {
      devWarn("stopSessionRecording failed", error);
    }
    return;
  }
  if (state.sessionReplayEligibleThisSession === null) {
    state.sessionReplayEligibleThisSession = Math.random() < sampleRate;
  }
  const eligible =
    state.sessionReplayEligibleThisSession && state.accountRole !== "ADMIN";
  try {
    if (eligible) {
      state.posthog?.startSessionRecording();
    } else {
      state.posthog?.stopSessionRecording();
    }
  } catch (error) {
    devWarn("session replay eligibility update failed", error);
  }
}

export function getAnalyticsConfig() {
  return config;
}

/** Test-only escape hatch to reset module state between test cases. */
export function __resetForTests() {
  state.posthog = null;
  state.initPromise = null;
  state.optedOut = false;
  state.excluded = false;
  state.accountRole = null;
  state.sessionReplayEligibleThisSession = null;
}
